import React, { useState } from 'react';
import { ArrowLeft, Trash2, Pencil, Check, Clock, Phone, MapPin, Building, Camera, ExternalLink } from 'lucide-react';
import { inr, netFee, balanceDue, formatDate } from './helpers';
import InspectionModal from './InspectionModal';
import PhotoPageEditor from './PhotoPageEditor';
import { srcForPath } from '@/lib/uploads';

export default function WorkDetail({ work: r, staff, staffById, currentUser, onClose, onEdit, onDelete, onSetFee, onPatch }) {
  const [tab, setTab] = useState('info');
  const [showInsp, setShowInsp] = useState(false);
  const st = r.staff_id ? staffById[r.staff_id] : null;
  const insp = r.inspected_by ? staffById[r.inspected_by] : null;
  const typeColor = r.work_type === 'Stage Bill' ? '#92400e' : r.work_type === 'Stability Certificate' ? '#15803d' : '#1e40af';
  const typeBg = r.work_type === 'Stage Bill' ? '#fef3c7' : r.work_type === 'Stability Certificate' ? '#dcfce7' : '#dbeafe';
  const ctClass = r.contact_type ? 'ct-' + r.contact_type.toLowerCase().replace(/\s+/g, '-') : '';

  const sectionTitle = (t) => <div style={{ fontSize: 10, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 10 }}>{t}</div>;
  const cardStyle = { background: '#fff', borderRadius: 10, border: '1px solid #e7e5e4', padding: 14 };
  const editBtn = (onClick, testid) => <button onClick={onClick} style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 4, border: '1px solid #1c1917', background: '#fff', color: '#1c1917', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid={testid}><Pencil className="icon" /> Edit</button>;

  const gpsHref = r.inspection_gps && (r.inspection_gps.startsWith('http') ? r.inspection_gps : `https://maps.google.com/?q=${encodeURIComponent(r.inspection_gps)}`);

  return (
    <div data-testid="work-detail">
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#44403c', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12, fontFamily: 'inherit' }} data-testid="detail-back"><ArrowLeft className="icon" /> Back to Works</button>

      <div className="status-tabs" style={{ maxWidth: 280 }}>
        <button className={`status-tab${tab === 'info' ? ' active' : ''}`} onClick={() => setTab('info')} data-testid="detail-tab-info">Info</button>
        <button className={`status-tab${tab === 'photos' ? ' active' : ''}`} onClick={() => setTab('photos')} data-testid="detail-tab-photos">Photos</button>
      </div>

      {tab === 'info' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => onDelete(r)} style={{ background: '#fff', color: '#dc2626', border: '1px solid #fecaca', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }} data-testid="detail-delete"><Trash2 className="icon" /> Delete Work</button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ background: typeBg, color: typeColor, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{r.work_type}</span>
            <span style={{ background: '#e7e5e4', color: '#44403c', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{r.office} Office</span>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>{sectionTitle('Client')}{editBtn(() => onEdit(r), 'detail-edit-client')}</div>
            {r.report_done && r.file_ref && (
              <div style={{ display: 'inline-block', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: '#7c2d12', background: '#fef3c7', border: '1px solid #fde68a', padding: '3px 9px', borderRadius: 4, marginBottom: 6, letterSpacing: '0.5px' }} data-testid="detail-fileref">{r.file_ref}</div>
            )}
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1c1917', letterSpacing: '-0.3px', marginBottom: 8 }}>{r.client_name || 'Name not added'}</div>
            {r.mobile && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13 }}><Phone className="icon" /> <a href={`tel:${r.mobile}`} style={{ color: '#1c1917', textDecoration: 'none', fontWeight: 600 }}>{r.mobile}</a>{r.contact_type && <span className={`contact-type-badge ${ctClass}`}>{r.contact_type}</span>}</div>}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', fontSize: 13, color: '#44403c' }}><MapPin className="icon" /> {r.location || 'Location not added'}</div>
            {r.work_type !== 'Stability Certificate' && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: '#44403c' }}><Building className="icon" /> <span><strong>{r.bank || 'Bank not added'}</strong>{r.branch ? ' — ' + r.branch : ''}</span></div>}
            {r.notes && (
              <div data-testid="detail-notes" style={{ marginTop: 8, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '3px solid #d97706', borderRadius: 4, fontSize: 12, color: '#78350f', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                📝 {r.notes}
              </div>
            )}
          </div>

          {/* Inspection card */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              {sectionTitle('Inspection')}
              <button onClick={() => setShowInsp(true)}
                style={{ fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 4,
                  border: '1px solid ' + (r.inspection_done ? '#1c1917' : '#16a34a'),
                  background: r.inspection_done ? '#fff' : '#16a34a',
                  color: r.inspection_done ? '#1c1917' : '#fff',
                  cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                data-testid="open-insp-modal">
                <Camera style={{ width: 12, height: 12 }} /> {r.inspection_done ? 'Edit Inspection' : 'Mark Inspection Done'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Row label="Status" value={<span style={{ color: r.inspection_done ? '#15803d' : '#b45309', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}>{r.inspection_done ? <><Check className="icon" /> Completed{insp ? ' · by ' + insp.name.split(' ')[0] : ''}</> : <><Clock className="icon" /> Not Completed</>}</span>} />
              {r.inspection_gps && (
                <Row label="GPS" value={
                  <a href={gpsHref} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1d4ed8', textDecoration: 'none', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    data-testid="detail-gps-link">
                    <MapPin className="icon" /> Open in Maps <ExternalLink style={{ width: 10, height: 10 }} />
                  </a>
                } />
              )}
              {Array.isArray(r.inspection_photos) && r.inspection_photos.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#78716c', fontWeight: 600, marginBottom: 6 }}>Photos ({r.inspection_photos.length})</div>
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }} data-testid="detail-insp-photos">
                    {r.inspection_photos.map((p, i) => (
                      <img key={p || `insp-${i}`} src={srcForPath(p)} alt={`insp-${i + 1}`} loading="lazy"
                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 5, border: '1px solid #e7e5e4', flex: 'none' }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={cardStyle}>
            {sectionTitle('Status')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Row label="Report" value={<span style={{ color: r.report_done ? '#15803d' : r.is_on_hold ? '#b45309' : '#78716c', fontWeight: 700 }}>{r.report_done ? 'Completed' : r.is_on_hold ? 'On Hold' : 'Not Started'}</span>} />
              <Row label="Staff Assigned" value={st ? st.name : 'Not assigned'} />
              <Row label="Created" value={formatDate(r.created_at)} />
            </div>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>{sectionTitle('Fee')}{editBtn(() => onSetFee(r), 'detail-edit-fee')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Row label="Total Fee" value={<span className="mono" style={{ fontWeight: 800 }}>{inr(r.fees || 0)}</span>} />
              {Number(r.discount || 0) > 0 && <Row label="Discount" value={<span className="mono" style={{ color: '#9a3412', fontWeight: 700 }}>−{inr(r.discount)}</span>} />}
              {Number(r.advance || 0) > 0 && <Row label="Advance" value={<span className="mono" style={{ fontWeight: 700 }}>{inr(r.advance)}</span>} />}
              <div style={{ borderTop: '1px dashed #e7e5e4', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 }}>
                <span style={{ color: '#1c1917', fontWeight: 800 }}>Balance</span>
                <span className="mono" style={{ color: '#1c1917', fontWeight: 800 }}>{inr(netFee(r) > 0 ? balanceDue(r) : 0)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <PhotoPageEditor work={r} onSave={onPatch} />
      )}

      {showInsp && (
        <InspectionModal work={r} staff={staff} currentUser={currentUser}
          onClose={() => setShowInsp(false)}
          onSave={async (w, patch) => { await onPatch(w, patch); setShowInsp(false); }} />
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
    <span style={{ color: '#44403c', fontWeight: 600 }}>{label}</span>
    <span style={{ color: '#1c1917', fontWeight: 700 }}>{value}</span>
  </div>
);
